
import { onMounted, reactive, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import SearchCondition from '@/components/block/SearchCondition.vue'
import SearchResultInformation from '@/components/block/SearchResultInformation.vue'
import HospitalsList from '@/components/block/HospitalsList.vue'
import HeadingLv1 from '@/components/elements/HeadingLv1.vue'
import Pager from '@/components/block/Pager.vue'
import Loader from '@/components/elements/Loader.vue'
import AdBanner, { AdBannerType } from '@/components/block/AdBanner.vue'
import { axiosSetting } from '@/utils/axiosSetting'
import { isPC } from '@/utils/commonMethods'
import { getSearchQuery, setSearchResultTitle } from '@/utils/searchMethods'
import { setSearchConditionCookie } from '@/utils/cookie'

export default {
  components: {
    SearchCondition,
    SearchResultInformation,
    HospitalsList,
    HeadingLv1,
    Pager,
    Loader,
    AdBanner
  },

  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const state = reactive({
      title: '',
      totalCount: 0,
      pageNum: 1,
      sortOption: 1,
      rowCnt: 15,
      showSearchConditionSm: false,
      isLoading: true
    })
    let lastScrollTop = 0
    let isBusy = false
    const list = reactive<any>([])

    // タイトルやディスクリプション設定
    const setMeta = () => {
      const result = setSearchResultTitle(
        route.path.replace('/search/latter/list', ''),
        'latter'
      )
      state.title = result.title

      const baseTitle =
        document.title.indexOf(' | ') > -1
          ? document.title.split(' | ')[1]
          : document.title
      document.title = result.title + ' | ' + baseTitle

      document
        .querySelector('meta[name="description"]')
        ?.setAttribute(
          'content',
          `${result.title}。出会いをつくることで、日本の医療に貢献したい。私たちはマイナビRESIDENTです。`
        )
    }

    // URLから検索APIのパラメータを作成する処理
    const currentParams = computed(() => {
      const params = route.path.replace('/search/latter/list/', '')
      const resObj = getSearchQuery(params, 'latter')

      if (resObj.free_word) {
        const res: any = {}

        res.free_word = resObj.free_word
        res.sort = String(state.sortOption)
        res.row_cnt = state.rowCnt

        if (resObj.page_no) res.page_no = resObj.page_no

        return res
      }

      resObj.sort = String(state.sortOption)
      resObj.row_cnt = state.rowCnt
      return resObj
    })

    // 最後に実行した検索条件をCookieに保存
    const saveLastSearchCondition = () => {
      setSearchConditionCookie('lastSearchLatterCondition', currentParams.value)
    }

    const ajaxApp = axiosSetting('app', 'block')
    const getHospitals = () => {
      state.isLoading = true

      if (list.length > 0) {
        list.splice(0, list.length)
        state.totalCount = 0
        state.pageNum = 1
      }

      const params = currentParams.value
      if (
        store.state.searchCoditionSaveFrg &&
        store.state.isLogin &&
        store.state.isSearchSubmit
      ) {
        params.save = true
        store.commit('updateSearchSubmit', false)
      }

      ajaxApp
        .post('/api/v1/s_search', params)
        .then((res) => {
          res.data.data.forEach((item: any) => list.push(item))
          state.totalCount = Number(res.data.total_num)
          state.pageNum = Number(res.data.page_num)
        })
        .catch((err: any) => console.log(err))
        .then(() => (state.isLoading = false))
    }

    // 有料バナー表示病院を取得して表示
    const adBanners = reactive<AdBannerType[]>([])
    const getAdBanners = () => {
      // 配列に入っているバナーを削除
      adBanners.splice(0)

      interface Params {
        [key: string]: string | Array<string> | undefined
        opt_id: string
        prefectures?: Array<string>
      }

      const params: Params = {
        opt_id: '3' // opt_idは3で固定
      }
      // 都道府県が検索に含まれていればパラメータにセット
      if (
        currentParams.value.prefectures &&
        currentParams.value.prefectures.length > 0
      ) {
        params.prefectures = currentParams.value.prefectures
      }

      ajaxApp
        .get('/api/v1/banner', {
          params: params
        })
        .then((res) => {
          // 広告バナーがなければ終了
          if (res.data.length === 0) return

          // 返却された病院データの中からランダムで1つ抽出してセット
          const ads = res.data
          const randomIndex: number = Math.floor(Math.random() * ads.length)
          adBanners.push(ads[randomIndex])
        })
    }

    // ページャーのページ遷移
    const movePage = (num: number) => {
      const path = route.path
      const regexp = /pg_\d*/g
      const matches = path.match(regexp)
      let nextPagePath = ''

      if (num > 1 && matches !== null)
        nextPagePath = path.replace(matches[0], `pg_${num}`)

      if (num === 1 && matches !== null)
        nextPagePath = path.replace(`/${matches[0]}`, '')

      if (num > 1 && matches === null) nextPagePath = `${path}/pg_${num}`

      if (num === 1 && matches === null) nextPagePath = path

      router.push(nextPagePath)
    }

    const changeSortOptions = (num: number) => {
      state.sortOption = num

      const path = route.path
      const regexp = /pg_\d*/g
      const matches = path.match(regexp)

      if (matches !== null) {
        router.push(path.replace(`${matches[0]}/`, ''))
      } else {
        getHospitals()
      }
    }

    watch(
      () => route.path,
      () => {
        if (route.name === 'LatterList') {
          getHospitals()
          getAdBanners()
          saveLastSearchCondition()
          setMeta()
        }
      }
    )

    onMounted(() => {
      // 初回実行
      getHospitals()
      saveLastSearchCondition()
      getAdBanners()
      setMeta()

      document.addEventListener('scroll', (e) => {
        if (isBusy || isPC()) return
        isBusy = true
        setTimeout(() => {
          isBusy = false
        }, 20)

        if (window.scrollY > 180 && window.scrollY > lastScrollTop) {
          state.showSearchConditionSm = true
        } else {
          state.showSearchConditionSm = false
        }

        lastScrollTop = window.scrollY
      })
    })

    return {
      list,
      state,
      currentParams,
      movePage,
      changeSortOptions,
      adBanners
    }
  }
}
